/**
 * Feature switches for the viewer. Every option is independently toggleable so a
 * host application can trade capability for bundle size, or lock the viewer down
 * further when rendering fully untrusted input.
 */
export interface ViewerOptions {
  /** Render ```mermaid fences as diagrams instead of code. */
  enableMermaid: boolean;
  /** Parse `$...$` / `$$...$$` and render with KaTeX. */
  enableMath: boolean;
  /** Allow raw HTML embedded in Markdown (still sanitized). */
  enableHtml: boolean;
  /** GFM pipe tables. */
  enableTables: boolean;
  /** GFM footnotes. */
  enableFootnotes: boolean;
  /** `Term` / `: Definition` description lists. */
  enableDefinitionLists: boolean;
  /** GitHub `> [!NOTE]` style callouts. */
  enableCallouts: boolean;
  /** Build and show the table of contents. */
  enableToc: boolean;
  /** In-document find with match highlighting. */
  enableSearch: boolean;
  /** Shiki syntax highlighting for fenced code. */
  enableSyntaxHighlighting: boolean;
  /**
   * Run the HAST sanitizer. Disabling this is only safe for fully trusted input
   * and is never done by the shipped application.
   */
  sanitizeHtml: boolean;
  /** Permit images pointing at other origins. */
  allowExternalImages: boolean;
  /** Permit links pointing at other origins. */
  allowExternalLinks: boolean;
  /** Render task list checkboxes as interactive (UI-only) controls. */
  interactiveTaskLists: boolean;
  /** Show line numbers in fenced code blocks. */
  showLineNumbers: boolean;
  /** Soft-wrap long lines in fenced code blocks. */
  wrapCode: boolean;
}

export const defaultViewerOptions: ViewerOptions = {
  enableMermaid: true,
  enableMath: true,
  enableHtml: true,
  enableTables: true,
  enableFootnotes: true,
  enableDefinitionLists: true,
  enableCallouts: true,
  enableToc: true,
  enableSearch: true,
  enableSyntaxHighlighting: true,
  sanitizeHtml: true,
  allowExternalImages: true,
  allowExternalLinks: true,
  interactiveTaskLists: true,
  showLineNumbers: false,
  wrapCode: false,
};

export const resolveViewerOptions = (
  overrides?: Partial<ViewerOptions>,
): ViewerOptions => ({ ...defaultViewerOptions, ...overrides });

export default defaultViewerOptions;